import { message, Tooltip, Flex, Tag } from 'antd';
import { ChevronRightIcon, CopyOUtlined } from '@/assets/icons/icons';
import { CustomParagraph, CustomTitle } from '@/components';
import HomeDrawerStatusTag from '../HomeDrawerStatusTag';
import TeamList from './TeamList';
import { truncateByLength, useToken } from '@/common/helper';
import { getCurrencySign } from '@/common/utils/utils';
import { ArrowRightOutlined } from '@ant-design/icons';
import { CYCLE_TIME_PHASES, type CyclePhaseKey } from './cycleTimePhases';
import { Shipment } from '../../types';
import {
  buildStatusPillStyle,
  getDeliveryStatusPillColors,
  neutralStatusPillColors,
} from '@/pages/Shipments/statusTagColors';
import { ControlTowerView } from '@/pages/Shipments/ControlTower';

interface ShipmentListItemProps {
  selectedCyclePhase?: CyclePhaseKey | null;
  isHome?: boolean;
  item: Shipment;
  onClick: (id: string, legType?: 'CL' | 'EL') => void;
  getFormattedTime: (date: string | null | undefined) => string;
  tabValue: string;
  chartType?: string;
  hideZeroAlerts?: boolean;
  customerLegEnabled?: boolean;
  metricValue?: { formatted: string; color: string };
}

/** When ``navigator.clipboard`` is missing (non-secure context / old browsers). */
function fallbackCopyTextToClipboard(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

const ShipmentListItem: React.FC<ShipmentListItemProps> = ({
  isHome = true,
  selectedCyclePhase,
  item,
  tabValue,
  chartType,
  hideZeroAlerts = false,
  customerLegEnabled = false,
  metricValue,
  onClick,
  getFormattedTime,
}) => {
  const { decodedUserToken } = useToken();
  const isDelivered = item?.status === 'DELIVERED';

  // Scope: only the Home shipment drawer (isHome) shows "Delivered:" whenever
  // an actual_delivery_date exists. Other consumers (e.g. CalendarViewMore)
  // keep the original type-based label to avoid behavior changes elsewhere.
  const hasActualDelivery = Boolean(item?.actual_delivery_date);
  const deliveryLabel = isHome
    ? hasActualDelivery
      ? 'Delivered:'
      : 'ETA:'
    : item?.type === 3
      ? 'Delivered:'
      : 'ETA:';
  const deliveryTime = getFormattedTime(
    (isHome ? hasActualDelivery : isDelivered)
      ? item?.actual_delivery_date
      : item?.estimated_delivery_date
  );

  const buildNamesString = (raw: unknown, keyCandidates: string[]): string => {
    let list: unknown = raw ?? [];
    if (typeof list === 'string') {
      try {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed)) {
          list = parsed;
        } else if (parsed) {
          list = [parsed];
        } else {
          list = [];
        }
      } catch {
        list = [list];
      }
    }
    if (!Array.isArray(list)) {
      list = [list];
    }

    const arr = Array.isArray(list) ? list : [];

    return arr
      .map((item) => {
        if (typeof item === 'string') return item;

        if (item && typeof item === 'object') {
          for (const key of keyCandidates) {
            const val = item[key as keyof typeof item];
            if (typeof val === 'string' && val.trim()) return val;
          }
        }

        return '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const renderCycleTimeBar = () => {
    const phases = item?.cycle_time_phases;
    if (!phases) return null;

    const total =
      Number(phases.pickup_hours || 0) +
      Number(phases.dwelling_hours || 0) +
      Number(phases.moving_hours || 0) +
      Number(phases.delivery_attempt_hours || 0) +
      Number(phases.last_mile_hours || 0);

    if (!total) return null;

    return (
      <div className='cycle-time-bar'>
        {CYCLE_TIME_PHASES.map((phase) => {
          const value = Number(item?.cycle_time_phases?.[phase.key] || 0);
          if (!value) return null;

          const shouldDim =
            selectedCyclePhase !== null && selectedCyclePhase !== phase.key;

          return (
            <div
              key={phase.key}
              className='cycle-time-bar-segment'
              style={{
                width: `${(value / total) * 100}%`,
                backgroundColor: phase.color,
                opacity: shouldDim ? 0.3 : 1,
              }}
            />
          );
        })}
      </div>
    );
  };

  const getSelectedCyclePhaseHours = () => {
    if (!selectedCyclePhase) return null;
    return Number(
      item?.cycle_time_phases?.[
        selectedCyclePhase as keyof NonNullable<typeof item.cycle_time_phases>
      ] || 0
    );
  };
  const renderChartTypeMetric = () => {
    if (!chartType) return null;

    if (chartType === 'cycle_time' && item?.cycle_time_hours != null) {
      const totalHours = Number(item.cycle_time_hours || 0);
      const selectedHours = getSelectedCyclePhaseHours();

      if (selectedCyclePhase && selectedHours !== null) {
        return (
          <div className='cycle-time-metric-block'>
            <span className='cycle-time-metric-primary'>
              {`${selectedHours.toFixed(0)} hrs`}
            </span>
            <span className='cycle-time-metric-secondary'>
              {`of ${totalHours.toFixed(0)} hrs total`}
            </span>
          </div>
        );
      }

      return (
        <CustomParagraph
          text={`${totalHours.toFixed(0)} hrs`}
          fontSize={12}
          fontWeight={400}
          lineHeight='20px'
          color='var(--text-light)'
          marginBottom='4px'
        />
      );
    }

    if (chartType === 'transit_time' && item?.transit_time_hours != null) {
      return (
        <CustomParagraph
          text={`${Number(item.transit_time_hours).toFixed(2)} hr`}
          fontSize={12}
          fontWeight={400}
          lineHeight='20px'
          color='var(--text-light)'
          marginBottom='4px'
        />
      );
    }

    if (chartType === 'dwell_time' && item?.dwell_time_hours != null) {
      return (
        <CustomParagraph
          text={`${Number(item.dwell_time_hours).toFixed(2)} hr`}
          fontSize={12}
          fontWeight={400}
          lineHeight='20px'
          color='var(--text-light)'
          marginBottom='4px'
        />
      );
    }

    if (chartType === 'alerts_deep_dive') {
      const count = item?.alert_count ?? 0;
      if (hideZeroAlerts && count <= 0) return null;

      const label = count === 1 ? 'alert' : 'alerts';

      const namesString = buildNamesString(item?.alert_status, [
        'alert_name',
        'name',
      ]);

      const content = (
        <span className='metric-dotted'>
          <CustomParagraph
            text={`${count} ${label}`}
            fontSize={12}
            fontWeight={400}
            lineHeight='20px'
            color='var(--text-light)'
            marginBottom={'0'}
          />
        </span>
      );

      return namesString ? (
        <Tooltip title={namesString}>{content}</Tooltip>
      ) : (
        content
      );
    }

    if (chartType === 'cost_deep_dive' && item?.cost_total != null) {
      const total = Number(item.cost_total);
      const rowCount = Number(item?.cost_row_count ?? 0);
      const formatted = `${getCurrencySign(decodedUserToken()?.currency)}${total.toLocaleString(
        'en-US',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;
      const tooltipText =
        rowCount > 0
          ? `${rowCount} cost ${rowCount === 1 ? 'entry' : 'entries'}`
          : '';

      const content = (
        <span className={rowCount > 1 ? 'metric-dotted' : ''}>
          <CustomParagraph
            text={formatted}
            fontSize={12}
            fontWeight={400}
            lineHeight='20px'
            color='var(--text-light)'
            marginBottom='4px'
          />
        </span>
      );

      return tooltipText ? (
        <Tooltip title={tooltipText}>{content}</Tooltip>
      ) : (
        content
      );
    }

    if (chartType === 'freight_markup_deep_dive' && item?.markup != null) {
      const markup = Number(item.markup);
      const currency = getCurrencySign(decodedUserToken()?.currency);
      const absMarkup = Math.abs(markup);
      const formatted = `${markup < 0 ? '-' : ''}${currency}${absMarkup.toLocaleString(
        'en-US',
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      )}`;
      const prepaid = Number(item?.prepaid_freight ?? 0);
      const carrierCost = Number(item?.carrier_cost ?? 0);
      const pct = item?.markup_pct;
      const tooltipText = [
        `Prepaid ${currency}${prepaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `Courier ${currency}${carrierCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        pct != null ? `Markup ${pct}%` : null,
      ]
        .filter(Boolean)
        .join(' · ');

      const content = (
        <span className='metric-dotted'>
          <CustomParagraph
            text={formatted}
            fontSize={12}
            fontWeight={400}
            lineHeight='20px'
            color='var(--text-light)'
            marginBottom='4px'
          />
        </span>
      );

      return <Tooltip title={tooltipText}>{content}</Tooltip>;
    }

    if (chartType === 'delivery_exceptions') {
      const count = item?.exception_count ?? 0;
      const label = count === 1 ? 'exception' : 'exceptions';

      const namesString = buildNamesString(item?.exception_list, [
        'exception_name',
        'name',
      ]);

      const content = (
        <span className='metric-dotted'>
          <CustomParagraph
            text={`${count} ${label}`}
            fontSize={12}
            fontWeight={400}
            lineHeight='20px'
            color='var(--text-light)'
            marginBottom='4px'
          />
        </span>
      );

      return namesString ? (
        <Tooltip title={namesString}>{content}</Tooltip>
      ) : (
        content
      );
    }

    return null;
  };

  const fromLocationName =
    item?.from_location?.name ||
    item?.from_location?.address ||
    'Unknown Location';

  const toLocationName =
    item?.to_location?.name || item?.to_location?.address || 'Unknown Location';

  const baseMetaLineText = `${deliveryLabel} ${deliveryTime} • ${fromLocationName} → ${toLocationName}`;

  const metaLine = (
    <>
      {metricValue ? `${metricValue.formatted} • ` : null}
      {deliveryLabel} {deliveryTime} • {fromLocationName}
      <ArrowRightOutlined width={12} height={12} style={{ margin: '0 4px' }} />
      {toLocationName}
    </>
  );

  type DeliveryStatus = 'on time' | 'early' | 'late';

  const STATUS_STYLES: Record<DeliveryStatus, { bg: string; color: string }> = {
    'on time': {
      bg: getDeliveryStatusPillColors('on time').backgroundColor,
      color: getDeliveryStatusPillColors('on time').color,
    },
    early: {
      bg: getDeliveryStatusPillColors('early').backgroundColor,
      color: getDeliveryStatusPillColors('early').color,
    },
    late: {
      bg: getDeliveryStatusPillColors('late').backgroundColor,
      color: getDeliveryStatusPillColors('late').color,
    },
  };

  const FALLBACK_STYLE = {
    bg: neutralStatusPillColors.backgroundColor,
    color: neutralStatusPillColors.color,
  };

  function getStatusStyle(statusRaw?: string) {
    if (!statusRaw) return FALLBACK_STYLE;

    const normalized = statusRaw.toLowerCase();

    if (normalized.includes('on time')) return STATUS_STYLES['on time'];
    if (normalized === 'delivered') {
      const colors = getDeliveryStatusPillColors('delivered');
      return { bg: colors.backgroundColor, color: colors.color };
    }
    if (normalized.includes('early')) return STATUS_STYLES['early'];
    if (normalized.includes('late')) return STATUS_STYLES['late'];

    return FALLBACK_STYLE;
  }

  const referenceText = String(item.reference_number ?? '').trim();
  const displayReference = truncateByLength(item.reference_number, 10);

  const handleCopyReference = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (!referenceText) return;

    const onCopied = () => message.success('Copied!');
    const onCopyFailed = () => message.error('Could not copy reference');

    if (navigator.clipboard && window.isSecureContext) {
      void navigator.clipboard
        .writeText(referenceText)
        .then(onCopied)
        .catch(() => {
          if (fallbackCopyTextToClipboard(referenceText)) {
            onCopied();
          } else {
            onCopyFailed();
          }
        });
      return;
    }

    if (fallbackCopyTextToClipboard(referenceText)) {
      onCopied();
    } else {
      onCopyFailed();
    }
  };

  const referenceTooltipTitle = referenceText ? (
    <span className='shipment-ref-tooltip-title'>
      <span className='shipment-ref-tooltip-title__text'>{referenceText}</span>
      <button
        type='button'
        className='shipment-ref-tooltip-copy'
        aria-label='Copy reference'
        onClick={handleCopyReference}
      >
        <CopyOUtlined
          width={13}
          height={15}
          color='rgba(255, 255, 255, 0.92)'
        />
      </button>
    </span>
  ) : undefined;

  const envView = process.env.REACT_APP_CONTROL_TOWER_VIEW as ControlTowerView;
  const isTableView = envView !== 'list';

  return (
    <div
      className='home-chart-detail-drawer-header-row-inner'
      onClick={() => {
        onClick(String(item.id), item.leg_type || 'CL');
      }}
    >
      <Flex justify='space-between' align='center' gap={8} wrap='wrap'>
        <Flex
          className='shipment-main-left'
          align='center'
          gap={30}
          style={{ flex: 1, minWidth: 0 }}
        >
          <Flex vertical className='shipment-text-block' style={{ flex: 1 }}>
            <Flex
              gap={8}
              className={` ${isTableView ? 'detail-list-wrapper' : ''} mb-1`}
            >
              <Tooltip
                title={referenceTooltipTitle}
                overlayClassName='shipment-ref-tooltip'
                color='#262626'
              >
                <span className='shipment-ref'>
                  <CustomTitle
                    text={displayReference}
                    whiteSpace='nowrap'
                    fontWeight='600'
                    fontSize='14px'
                    lineHeight='16px'
                    color='var(--text-primary)'
                    marginTop={0}
                    marginBottom={0}
                  />
                </span>
              </Tooltip>
              <Flex gap={4} className='team-alert-wrapper'>
                <HomeDrawerStatusTag
                  type={item?.type}
                  status={item?.status}
                  alert_status={item?.alert_status}
                  customerLegEnabled={customerLegEnabled}
                />

                <TeamList items={item} />
              </Flex>
            </Flex>

            <Tooltip title={metaLine}>
              <div className='meta-ellipsis meta-custom-title'>
                {metricValue ? (
                  <span
                    className='home-report-shipment-metric-value'
                    style={{ color: metricValue.color }}
                  >
                    {metricValue.formatted}
                  </span>
                ) : null}
                {metricValue ? ' • ' : null}
                {baseMetaLineText}
              </div>
            </Tooltip>

            {chartType === 'cycle_time' && renderCycleTimeBar()}
          </Flex>

          {isHome && (
            <div className='shipment-metric'>
              {item?.delivery_status && tabValue == 'timeliness' && (
                <Tag
                  className='shipment-delay-tag'
                  style={buildStatusPillStyle(
                    getDeliveryStatusPillColors(item.delivery_status),
                    {
                      borderRadius: '999px',
                      padding: '2px 8px',
                      fontSize: '10px',
                      lineHeight: '16px',
                    }
                  )}
                >
                  {item?.delivery_status}
                </Tag>
              )}

              {item?.delivery_status &&
                (chartType === 'otd_distribution' ||
                  chartType === 'otd_trend') && (
                  <Tag
                    className='shipment-delay-tag'
                    {...(() => {
                      const style = getStatusStyle(item?.delivery_status);

                      return {
                        style: {
                          backgroundColor: style?.bg,
                          borderColor: style?.bg,
                          borderRadius: '4px',
                          padding: '2px 8px',
                          color: style?.color,
                          fontSize: '10px',
                          fontWeight: '600',
                          lineHeight: '16px',
                        },
                      };
                    })()}
                  >
                    {item.delivery_status}
                  </Tag>
                )}

              {tabValue === 'risk' && (
                <CustomParagraph
                  text={
                    item?.alert_type == 0
                      ? 'Critical'
                      : item?.alert_type == 1
                        ? 'At-risk'
                        : 'No Issue'
                  }
                  fontSize={12}
                  fontWeight={400}
                  lineHeight='20px'
                  color='var(--text-light)'
                  marginBottom='4px'
                />
              )}

              {renderChartTypeMetric()}
            </div>
          )}
        </Flex>

        <button className='zl-btn zl-radius-full zl-transparent-btn-hover shipment-chevron'>
          <ChevronRightIcon width={16} height={16} fill='var(--text-primary)' />
        </button>
      </Flex>
    </div>
  );
};

export default ShipmentListItem;
